import React, { useState } from 'react';
import { Check, X, Power, PowerOff, UserCog } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { UserRoleType } from '../interfaces/User';

interface UserBulkActionsProps {
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
}

const UserBulkActions: React.FC<UserBulkActionsProps> = ({
  selectedIds,
  onSelectionChange,
}) => {
  const { state, actions } = useUser();
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    role: '',
    is_active: '',
  });

  const selectedUsers = (state.users || []).filter(user => selectedIds.includes(user.id));

  const roleOptions = [
    { value: '', label: 'Não alterar' },
    { value: 'admin', label: 'Administrador' },
    { value: 'gestor', label: 'Gestor' },
    { value: 'emissor', label: 'Emissor' },
    { value: 'reader', label: 'Leitor' },
  ];

  const handleBulkActivate = async () => {
    if (!window.confirm(`Ativar ${selectedIds.length} usuário(s) selecionado(s)?`)) {
      return;
    }

    try {
      await actions.bulkActivate(selectedIds);
      onSelectionChange([]);
    } catch (error) {
      console.error('Erro na ativação em lote:', error);
    }
  };

  const handleBulkDeactivate = async () => {
    if (!window.confirm(`Desativar ${selectedIds.length} usuário(s) selecionado(s)?`)) {
      return;
    }

    try {
      await actions.bulkDeactivate(selectedIds);
      onSelectionChange([]);
    } catch (error) {
      console.error('Erro na desativação em lote:', error);
    }
  };

  const handleBulkEdit = async () => {
    if (!window.confirm(`Aplicar mudanças a ${selectedIds.length} usuário(s) selecionado(s)?`)) {
      return;
    }

    if (bulkEditData.role) {
      try {
        await actions.bulkChangeRole(selectedIds, bulkEditData.role as UserRoleType);
      } catch (error) {
        console.error('Erro na alteração de role em lote:', error);
        return;
      }
    }

    if (bulkEditData.is_active !== '') {
      try {
        if (bulkEditData.is_active === 'true') {
          await actions.bulkActivate(selectedIds);
        } else {
          await actions.bulkDeactivate(selectedIds);
        }
      } catch (error) {
        console.error('Erro na alteração de status em lote:', error);
        return;
      }
    }

    setShowBulkEdit(false);
    setBulkEditData({ role: '', is_active: '' });
    onSelectionChange([]);
  };

  if (selectedIds.length === 0) {
    return null;
  }

  return (
    <div className="card" style={{ 
      marginBottom: '20px',
      background: 'var(--color-lightblue)',
      border: '1px solid var(--color-blue)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <strong>{selectedIds.length} usuário(s) selecionado(s)</strong>
          <p style={{ margin: '4px 0 0 0', fontSize: 'var(--font-size-14)', color: 'var(--color-darkgray)' }}>
            {selectedUsers.filter(u => u.is_active).length} ativo(s), {selectedUsers.filter(u => !u.is_active).length} inativo(s)
            <br />
            Admin: {selectedUsers.filter(u => u.role === 'admin').length}, 
            Gestor: {selectedUsers.filter(u => u.role === 'gestor').length}, 
            Emissor: {selectedUsers.filter(u => u.role === 'emissor').length}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={handleBulkActivate}
            className="btn btn-success"
            style={{ padding: '6px 12px' }}
            title="Ativar selecionados"
          >
            <Power size={14} />
            Ativar
          </button>

          <button
            onClick={handleBulkDeactivate}
            className="btn btn-danger"
            style={{ padding: '6px 12px' }}
            title="Desativar selecionados"
          >
            <PowerOff size={14} />
            Desativar
          </button>

          <button
            onClick={() => setShowBulkEdit(!showBulkEdit)}
            className="btn btn-secondary"
            style={{ padding: '6px 12px' }}
            title="Edição em lote"
          >
            <UserCog size={14} />
            Editar
          </button>

          <button
            onClick={() => onSelectionChange([])}
            className="btn btn-outline"
            style={{ padding: '6px 12px' }}
            title="Limpar seleção"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {showBulkEdit && (
        <div style={{ 
          marginTop: '16px', 
          padding: '16px', 
          background: 'white', 
          borderRadius: 'var(--br-5)',
          border: '1px solid var(--color-lightgray)'
        }}>
          <h4 style={{ margin: '0 0 16px 0' }}>Edição em Lote</h4>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div className="form-group">
              <label className="form-label">Role (deixe vazio para não alterar)</label>
              <select
                value={bulkEditData.role}
                onChange={(e) => setBulkEditData({ ...bulkEditData, role: e.target.value })}
                className="form-select"
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status (deixe vazio para não alterar)</label>
              <select
                value={bulkEditData.is_active}
                onChange={(e) => setBulkEditData({ ...bulkEditData, is_active: e.target.value })}
                className="form-select"
              >
                <option value="">Não alterar</option>
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleBulkEdit}
              className="btn btn-primary"
              style={{ padding: '8px 16px' }}
            >
              <Check size={14} />
              Aplicar Mudanças
            </button>
            
            <button
              onClick={() => setShowBulkEdit(false)}
              className="btn btn-secondary"
              style={{ padding: '8px 16px' }}
            >
              <X size={14} />
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBulkActions;